import { useCallback, useMemo, useRef } from 'react';
import { List, Empty, Card } from 'antd';
import { useAntdTable } from 'ahooks';
import cls from 'classnames';
import commonStyles from '../common.module.less';
import styles from './index.module.less';
import type { FullTextSearchRecord } from '@/api/sip/bookmap/searchAll';
import { fullTextSearchUsingPost } from '@/api/sip/bookmap/searchAll';
import type { EventEmitter } from 'ahooks/lib/useEventEmitter';
import type { SelectData } from '../../../CatalogTree';
import type { ManualRecord } from '@/api/sip/bookmap/list';
import SearchBar from '../SearchBar';
import { useTranslation } from '@/hooks/useTranslation';
import useURLParams from '@/hooks/useURLParam';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';

export type TContentProps = {
  className?: string;
  scrollToTop?: () => void;
  searchSelect$: EventEmitter<SelectData>;
  manuaList: ManualRecord[];
};

const Content: React.FC<TContentProps> = props => {
  const $ = useTranslation();
  const {
    params: { modelCode }
  } = useURLParams();
  const lang = useSelector<RootState, string>(state => state.LocaleModel.serverLang, shallowEqual);

  const { className } = props;
  const keywordRef = useRef<string>();

  const { loading, tableProps, run, search } = useAntdTable(
    async tableParams => {
      const resp = await fullTextSearchUsingPost({
        keyword: keywordRef.current ?? '',
        currentPage: tableParams.current,
        size: tableParams.pageSize,
        bookmapCodeList: props.manuaList.map(item => item.bookmapCode),
        lang,
        modelCode
      });
      if (resp.code === 200) {
        return {
          list: resp.data.records,
          total: resp.data.total
        };
      } else {
        return {
          list: [],
          total: 0
        };
      }
    },
    {
      defaultPageSize: 10,
      debounceWait: 500,
      debounceLeading: true,
      manual: true
    }
  );

  const locale = useMemo(() => {
    return {
      emptyText: (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={loading ? $('加载中...') : $('暂无数据')}
        />
      )
    };
  }, [loading, $]);

  const scrollToTop = props.scrollToTop;
  const pagination = useMemo(() => {
    return {
      ...tableProps.pagination,
      onChange: (current, pageSize) => {
        run({ current, pageSize });
        scrollToTop?.();
      }
    };
  }, [run, tableProps.pagination, scrollToTop]);

  const handleCardClick = useCallback(
    (item: FullTextSearchRecord) => {
      props.searchSelect$.emit({
        uniqueCode: item.uniqueCode,
        code: item.topicCode,
        name: item.topicName,
        bookMapCode: item.bookmapCode,
        version: item.topicVersion
      });
    },
    [props.searchSelect$]
  );

  const handleKeyWordSearch = useCallback(
    (value: string) => {
      if (keywordRef.current !== value) {
        keywordRef.current = value;
        search.submit();
      }
    },
    [search]
  );

  return (
    <>
      <SearchBar onSearch={handleKeyWordSearch} className={styles.searchInput} />
      <div className={cls(commonStyles.card, className)}>
        <List
          {...tableProps}
          pagination={pagination}
          loading={loading}
          renderItem={item => (
            <Card
              title={
                <div style={{ lineHeight: '30px', padding: '16px 0' }}>
                  <span
                    className={cls(styles.highlight)}
                    dangerouslySetInnerHTML={{ __html: item.topicName || '-' }}
                  />
                  <br />
                  <span
                    style={{ fontSize: '13px', fontWeight: 'normal', color: '#171F26' }}
                    className={cls(styles.highlight)}
                    dangerouslySetInnerHTML={{ __html: item.path || '-' }}
                  />
                </div>
              }
              className={cls(styles.container, styles.caseItem)}
              onClick={() => handleCardClick(item)}
            >
              <span
                className={cls(styles.fieldContent, styles.highlight)}
                dangerouslySetInnerHTML={{
                  __html: item.content || '-'
                }}
              />
            </Card>
          )}
          rowKey={item => item.topicCode}
          locale={locale}
        />
      </div>
    </>
  );
};

export default Content;
